import { useQuery } from "@tanstack/react-query";
import { Navigate, createRootRoute, createRoute, createRouter, lazyRouteComponent } from "@tanstack/react-router";
import { api, normalizeSession } from "./api";
import { AppShell } from "./components/AppShell";
import { ErrorState, LoadingState } from "./components/UI";
import { LoginPage } from "./pages/LoginPage";

const ActivityPage = lazyRouteComponent(() => import("./pages/ActivityPage"), "ActivityPage");
const AdminsPage = lazyRouteComponent(() => import("./pages/AdminsPage"), "AdminsPage");
const BusinessPage = lazyRouteComponent(() => import("./pages/BusinessPage"), "BusinessPage");
const CommercialOperationsPage = lazyRouteComponent(() => import("./pages/CommercialOperationsPage"), "CommercialOperationsPage");
const DashboardPage = lazyRouteComponent(() => import("./pages/DashboardPage"), "DashboardPage");
const EvaluationRequestsPage = lazyRouteComponent(() => import("./pages/EvaluationRequestsPage"), "EvaluationRequestsPage");
const InvitesPage = lazyRouteComponent(() => import("./pages/InvitesPage"), "InvitesPage");
const MapDetailPage = lazyRouteComponent(() => import("./pages/MapDetailPage"), "MapDetailPage");
const MapsPage = lazyRouteComponent(() => import("./pages/MapsPage"), "MapsPage");
const ObjectsPage = lazyRouteComponent(() => import("./pages/ObjectsPage"), "ObjectsPage");
const OrganizationDetailPage = lazyRouteComponent(() => import("./pages/OrganizationDetailPage"), "OrganizationDetailPage");
const OrganizationsPage = lazyRouteComponent(() => import("./pages/OrganizationsPage"), "OrganizationsPage");
const UserDetailPage = lazyRouteComponent(() => import("./pages/UserDetailPage"), "UserDetailPage");
const UsersPage = lazyRouteComponent(() => import("./pages/UsersPage"), "UsersPage");

function RootComponent() {
  const session = useQuery({
    queryKey: ["session"],
    queryFn: async () => normalizeSession(await api<unknown>("/api/session")),
    retry: (count, error) => !(error instanceof Error && "status" in error && (error as { status?: number }).status === 401) && count < 2,
    staleTime: 30_000,
  });
  if (session.isLoading) return <div className="boot-screen"><LoadingState label="Opening the operations room" /></div>;
  if (session.isError) return <div className="boot-screen"><ErrorState error={session.error} onRetry={() => void session.refetch()} /></div>;
  if (!session.data?.authenticated) return <LoginPage />;
  return <AppShell session={session.data} />;
}

function NotFoundPage() {
  return <div className="not-found"><span>404</span><h1>This trail ends here.</h1><p>The page may have moved, or the record may no longer be available.</p><a className="button button-primary button-medium" href="/">Return to mission control</a></div>;
}

const rootRoute = createRootRoute({ component: RootComponent, notFoundComponent: NotFoundPage });
const dashboardRoute = createRoute({ getParentRoute: () => rootRoute, path: "/", component: DashboardPage });
const loginRoute = createRoute({ getParentRoute: () => rootRoute, path: "/login", component: () => <Navigate to="/" replace /> });
const usersRoute = createRoute({ getParentRoute: () => rootRoute, path: "/users", component: UsersPage });
const invitesRoute = createRoute({ getParentRoute: () => rootRoute, path: "/invites", component: InvitesPage });
const evaluationRequestsRoute = createRoute({ getParentRoute: () => rootRoute, path: "/evaluation-requests", component: EvaluationRequestsPage });
const userDetailRoute = createRoute({ getParentRoute: () => rootRoute, path: "/users/$userId", component: UserDetailPage });
const organizationsRoute = createRoute({ getParentRoute: () => rootRoute, path: "/organizations", component: OrganizationsPage });
const organizationDetailRoute = createRoute({ getParentRoute: () => rootRoute, path: "/organizations/$organizationId", component: OrganizationDetailPage });
const mapsRoute = createRoute({ getParentRoute: () => rootRoute, path: "/maps", component: MapsPage });
const mapDetailRoute = createRoute({ getParentRoute: () => rootRoute, path: "/maps/$mapId", component: MapDetailPage });
const objectsRoute = createRoute({ getParentRoute: () => rootRoute, path: "/objects", component: ObjectsPage });
const activityRoute = createRoute({ getParentRoute: () => rootRoute, path: "/activity", component: ActivityPage });
const adminsRoute = createRoute({ getParentRoute: () => rootRoute, path: "/admins", component: AdminsPage });
const businessRoute = createRoute({ getParentRoute: () => rootRoute, path: "/business", component: BusinessPage });
const businessDocumentRoute = createRoute({ getParentRoute: () => rootRoute, path: "/business/$documentId", component: BusinessPage });
const commercialOperationsRoute = createRoute({ getParentRoute: () => rootRoute, path: "/commercial", component: CommercialOperationsPage });

const routeTree = rootRoute.addChildren([
  dashboardRoute,
  loginRoute,
  usersRoute,
  invitesRoute,
  userDetailRoute,
  evaluationRequestsRoute,
  organizationsRoute,
  organizationDetailRoute,
  mapsRoute,
  mapDetailRoute,
  objectsRoute,
  activityRoute,
  adminsRoute,
  commercialOperationsRoute,
  businessRoute,
  businessDocumentRoute,
]);

export const router = createRouter({ routeTree, defaultPreload: "intent", defaultPreloadStaleTime: 20_000 });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
